import React from 'react';
import { Textarea } from '@roger/ui-components';
import { WaystarResponse } from '../types/x12';

interface X12_271_Response_DisplayProps {
  waystarResponse: WaystarResponse;
}

const X12_271_Response_Display: React.FC<X12_271_Response_DisplayProps> = ({
  waystarResponse,
}) => {
  return (
    <>
      <div>
        <label className='block text-sm font-medium mb-2'>HTTP Status</label>
        <div
          className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
            waystarResponse.http_status >= 200 &&
            waystarResponse.http_status < 300
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {waystarResponse.http_status}
        </div>
      </div>

      {waystarResponse.x12_271_message && (
        <div>
          <label className='block text-sm font-medium mb-2'>
            X12 271 Response
          </label>
          <Textarea
            value={waystarResponse.x12_271_message}
            readOnly
            className='font-mono text-xs h-32'
            placeholder='X12 271 response will appear here...'
          />
        </div>
      )}

      {waystarResponse.parsed_271_response && (
        <div>
          <label className='block text-sm font-medium mb-2'>
            Parsed X12 271 Response
          </label>
          <div className='space-y-4 p-4 bg-gray-50 rounded-lg'>
            {waystarResponse.parsed_271_response.inquiry_id && (
              <div>
                <span className='font-medium'>Inquiry ID:</span>{' '}
                {waystarResponse.parsed_271_response.inquiry_id}
              </div>
            )}

            {/* Enhanced Subscriber Information */}
            {waystarResponse.parsed_271_response.subscriber_info &&
              waystarResponse.parsed_271_response.subscriber_info.length >
                0 && (
                <div>
                  <span className='font-medium text-md'>
                    Subscriber Information:
                  </span>
                  {waystarResponse.parsed_271_response.subscriber_info.map(
                    (subscriber, index) => (
                      <div
                        key={index}
                        className='mt-3 p-4 bg-white rounded-lg border'
                      >
                        {/* Name Information */}
                        {subscriber.name &&
                          Object.keys(subscriber.name).length > 0 && (
                            <div className='mb-3 text-sm'>
                              <h4 className='font-medium text-sm mb-2'>
                                Personal Information
                              </h4>
                              <div className='grid grid-cols-2 gap-4 text-sm'>
                                {subscriber.name.full_name && (
                                  <div>
                                    <span className='font-medium'>Name:</span>{' '}
                                    {subscriber.name.full_name}
                                  </div>
                                )}
                                {subscriber.name.id_code && (
                                  <div>
                                    <span className='font-medium'>
                                      Member ID:
                                    </span>{' '}
                                    {subscriber.name.id_code}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                        {/* Demographics */}
                        {subscriber.demographics &&
                          Object.keys(subscriber.demographics).length > 0 && (
                            <div className='mb-3'>
                              <div className='grid grid-cols-2 gap-4 text-sm'>
                                {subscriber.demographics.formatted_dob && (
                                  <div>
                                    <span className='font-medium'>
                                      Date of Birth:
                                    </span>{' '}
                                    {subscriber.demographics.formatted_dob}
                                  </div>
                                )}
                                {subscriber.demographics.gender && (
                                  <div>
                                    <span className='font-medium'>Gender:</span>{' '}
                                    {subscriber.demographics.gender === 'M'
                                      ? 'Male'
                                      : subscriber.demographics.gender === 'F'
                                        ? 'Female'
                                        : subscriber.demographics.gender}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                        {/* Address Information */}
                        {subscriber.address &&
                          subscriber.address.formatted_address && (
                            <div className='mb-3'>
                              <h4 className='font-medium text-md mb-2'>
                                Address
                              </h4>
                              <div className='text-sm whitespace-pre-line'>
                                {subscriber.address.formatted_address}
                              </div>
                            </div>
                          )}

                        {/* Contact Information */}
                        {subscriber.contacts &&
                          subscriber.contacts.length > 0 && (
                            <div className='mb-3'>
                              <h4 className='font-medium text-md mb-2'>
                                Contact Information
                              </h4>
                              <div className='space-y-2'>
                                {subscriber.contacts.map(
                                  (contact, contactIndex) => (
                                    <div
                                      key={contactIndex}
                                      className='text-sm p-2 bg-gray-50 rounded'
                                    >
                                      {contact.contact_name && (
                                        <div>
                                          <span className='font-medium'>
                                            Contact:
                                          </span>{' '}
                                          {contact.contact_name}
                                        </div>
                                      )}
                                      {contact.communication_number1 && (
                                        <div>
                                          <span className='font-medium'>
                                            Phone:
                                          </span>{' '}
                                          {contact.communication_number1}
                                        </div>
                                      )}
                                      {contact.communication_number2 && (
                                        <div>
                                          <span className='font-medium'>
                                            Alt Phone:
                                          </span>{' '}
                                          {contact.communication_number2}
                                        </div>
                                      )}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                        {/* Important Dates */}
                        {subscriber.dates && subscriber.dates.length > 0 && (
                          <div className='mb-3'>
                            <h4 className='font-medium text-md mb-2'>
                              Important Dates
                            </h4>
                            <div className='space-y-1'>
                              {subscriber.dates.map((date, dateIndex) => (
                                <div key={dateIndex} className='text-sm'>
                                  <span className='font-medium'>
                                    {date.date_qualifier_description}:
                                  </span>{' '}
                                  {date.formatted_date || date.date_value}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Messages */}
                        {subscriber.messages &&
                          subscriber.messages.length > 0 && (
                            <div className='mb-3'>
                              <h4 className='font-medium text-md mb-2'>
                                Messages
                              </h4>
                              <div className='space-y-1'>
                                {subscriber.messages.map(
                                  (message, msgIndex) => (
                                    <div
                                      key={msgIndex}
                                      className='text-sm p-2 bg-yellow-50 border border-yellow-200 rounded'
                                    >
                                      {message.message_text}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                        {/* References */}
                        {subscriber.references &&
                          subscriber.references.length > 0 && (
                            <div className='mb-3'>
                              <h4 className='font-medium text-md mb-2'>
                                Reference Information
                              </h4>
                              <div className='space-y-1'>
                                {subscriber.references.map((ref, refIndex) => (
                                  <div key={refIndex} className='text-sm'>
                                    <span className='font-medium'>
                                      {ref.reference_id_qualifier_description}:
                                    </span>{' '}
                                    {ref.reference_id}
                                    {ref.description && (
                                      <span> - {ref.description}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                        {/* Eligibility Information */}
                        {subscriber.eligibility &&
                          subscriber.eligibility.length > 0 && (
                            <div className='mb-3'>
                              <h4 className='font-medium text-md mb-2'>
                                Eligibility & Benefits
                              </h4>
                              <div className='space-y-2'>
                                {subscriber.eligibility.map(
                                  (eligibility, eligIndex) => (
                                    <div
                                      key={eligIndex}
                                      className='p-3 bg-blue-50 border border-blue-200 rounded'
                                    >
                                      <div className='grid grid-cols-2 gap-4 text-sm'>
                                        <div>
                                          <span className='font-medium'>
                                            Service:
                                          </span>{' '}
                                          {eligibility.service_type_description ||
                                            eligibility.service_type}
                                        </div>
                                        <div>
                                          <span className='font-medium'>
                                            Coverage:
                                          </span>{' '}
                                          {eligibility.coverage_level_description ||
                                            eligibility.coverage_level}
                                        </div>
                                        <div>
                                          <span className='font-medium'>
                                            Insurance:
                                          </span>{' '}
                                          {eligibility.insurance_type_description ||
                                            eligibility.insurance_type}
                                        </div>
                                        {eligibility.monetary_amount && (
                                          <div>
                                            <span className='font-medium'>
                                              Amount:
                                            </span>{' '}
                                            ${eligibility.monetary_amount}
                                          </div>
                                        )}
                                        {eligibility.percentage && (
                                          <div>
                                            <span className='font-medium'>
                                              Percentage:
                                            </span>{' '}
                                            {eligibility.percentage}%
                                          </div>
                                        )}
                                        {eligibility.authorization_indicator && (
                                          <div>
                                            <span className='font-medium'>
                                              Auth Required:
                                            </span>{' '}
                                            {eligibility.authorization_indicator ===
                                            'Y'
                                              ? 'Yes'
                                              : 'No'}
                                          </div>
                                        )}
                                      </div>
                                      {eligibility.insurance_plan_description && (
                                        <div className='mt-2 text-sm'>
                                          <span className='font-medium'>
                                            Plan:
                                          </span>{' '}
                                          {
                                            eligibility.insurance_plan_description
                                          }
                                        </div>
                                      )}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                        {/* Additional Information */}
                        {subscriber.additional_info &&
                          subscriber.additional_info.length > 0 && (
                            <div className='mb-3'>
                              <h4 className='font-medium text-md mb-2'>
                                Additional Information
                              </h4>
                              <div className='space-y-1'>
                                {subscriber.additional_info.map(
                                  (info, infoIndex) => (
                                    <div key={infoIndex} className='text-sm'>
                                      <span className='font-medium'>
                                        {info.description}:
                                      </span>{' '}
                                      {info.industry_code}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    )
                  )}
                </div>
              )}

            {/* Enhanced Dependent Information */}
            {waystarResponse.parsed_271_response.dependent_info &&
              waystarResponse.parsed_271_response.dependent_info.length > 0 && (
                <div>
                  <span className='font-medium text-md'>
                    Dependent Information:
                  </span>
                  {waystarResponse.parsed_271_response.dependent_info.map(
                    (dependent, index) => (
                      <div
                        key={index}
                        className='mt-3 p-4 bg-white rounded-lg border'
                      >
                        {/* Same structure as subscriber but for dependents */}
                        {dependent.name &&
                          Object.keys(dependent.name).length > 0 && (
                            <div className='mb-3'>
                              <h4 className='font-medium text-md mb-2'>
                                Personal Information
                              </h4>
                              <div className='grid grid-cols-2 gap-4 text-sm'>
                                {dependent.name.full_name && (
                                  <div>
                                    <span className='font-medium'>Name:</span>{' '}
                                    {dependent.name.full_name}
                                  </div>
                                )}
                                {dependent.name.id_code && (
                                  <div>
                                    <span className='font-medium'>
                                      Member ID:
                                    </span>{' '}
                                    {dependent.name.id_code}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                        {dependent.demographics &&
                          Object.keys(dependent.demographics).length > 0 && (
                            <div className='mb-3'>
                              <div className='grid grid-cols-2 gap-4 text-sm'>
                                {dependent.demographics.formatted_dob && (
                                  <div>
                                    <span className='font-medium'>
                                      Date of Birth:
                                    </span>{' '}
                                    {dependent.demographics.formatted_dob}
                                  </div>
                                )}
                                {dependent.demographics.gender && (
                                  <div>
                                    <span className='font-medium'>Gender:</span>{' '}
                                    {dependent.demographics.gender === 'M'
                                      ? 'Male'
                                      : dependent.demographics.gender === 'F'
                                        ? 'Female'
                                        : dependent.demographics.gender}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                        {dependent.eligibility &&
                          dependent.eligibility.length > 0 && (
                            <div className='mb-3'>
                              <h4 className='font-medium text-md mb-2'>
                                Eligibility & Benefits
                              </h4>
                              <div className='space-y-2'>
                                {dependent.eligibility.map(
                                  (eligibility, eligIndex) => (
                                    <div
                                      key={eligIndex}
                                      className='p-3 bg-green-50 border border-green-200 rounded'
                                    >
                                      <div className='grid grid-cols-2 gap-4 text-sm'>
                                        <div>
                                          <span className='font-medium'>
                                            Service:
                                          </span>{' '}
                                          {eligibility.service_type_description ||
                                            eligibility.service_type}
                                        </div>
                                        <div>
                                          <span className='font-medium'>
                                            Coverage:
                                          </span>{' '}
                                          {eligibility.coverage_level_description ||
                                            eligibility.coverage_level}
                                        </div>
                                        <div>
                                          <span className='font-medium'>
                                            Insurance:
                                          </span>{' '}
                                          {eligibility.insurance_type_description ||
                                            eligibility.insurance_type}
                                        </div>
                                        {eligibility.monetary_amount && (
                                          <div>
                                            <span className='font-medium'>
                                              Amount:
                                            </span>{' '}
                                            ${eligibility.monetary_amount}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    )
                  )}
                </div>
              )}

            {/* Legacy Subscriber Eligibility (for backward compatibility) */}
            {waystarResponse.parsed_271_response.subscriber_eligibility.length >
              0 &&
              (!waystarResponse.parsed_271_response.subscriber_info ||
                waystarResponse.parsed_271_response.subscriber_info.length ===
                  0) && (
                <div>
                  <span className='font-medium'>
                    Subscriber Eligibility (Legacy):
                  </span>
                  <div className='mt-2 space-y-2'>
                    {waystarResponse.parsed_271_response.subscriber_eligibility.map(
                      (eligibility, index) => (
                        <div
                          key={index}
                          className='p-2 bg-white rounded border'
                        >
                          <div className='text-sm'>
                            <span className='font-medium'>Service Type:</span>{' '}
                            {eligibility.service_type}
                          </div>
                          <div className='text-sm'>
                            <span className='font-medium'>Coverage Level:</span>{' '}
                            {eligibility.coverage_level}
                          </div>
                          <div className='text-sm'>
                            <span className='font-medium'>Insurance Type:</span>{' '}
                            {eligibility.insurance_type}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

            {/* Legacy Dependent Eligibility (for backward compatibility) */}
            {waystarResponse.parsed_271_response.dependent_eligibility.length >
              0 &&
              (!waystarResponse.parsed_271_response.dependent_info ||
                waystarResponse.parsed_271_response.dependent_info.length ===
                  0) && (
                <div>
                  <span className='font-medium'>
                    Dependent Eligibility (Legacy):
                  </span>
                  <div className='mt-2 space-y-2'>
                    {waystarResponse.parsed_271_response.dependent_eligibility.map(
                      (eligibility, index) => (
                        <div
                          key={index}
                          className='p-2 bg-white rounded border'
                        >
                          <div className='text-sm'>
                            <span className='font-medium'>Service Type:</span>{' '}
                            {eligibility.service_type}
                          </div>
                          <div className='text-sm'>
                            <span className='font-medium'>Coverage Level:</span>{' '}
                            {eligibility.coverage_level}
                          </div>
                          <div className='text-sm'>
                            <span className='font-medium'>Insurance Type:</span>{' '}
                            {eligibility.insurance_type}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

            {waystarResponse.parsed_271_response.errors.length > 0 && (
              <div>
                <span className='font-medium text-red-600'>
                  Parsing Errors:
                </span>
                <ul className='mt-2 list-disc list-inside text-red-600'>
                  {waystarResponse.parsed_271_response.errors.map(
                    (error, index) => (
                      <li key={index}>{error}</li>
                    )
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {waystarResponse.waystar_alerts &&
        waystarResponse.waystar_alerts.length > 0 && (
          <div>
            <label className='block text-sm font-medium mb-2'>
              Waystar Alerts
            </label>
            <div className='p-4 bg-yellow-50 border border-yellow-200 rounded-lg'>
              <div className='text-yellow-800 whitespace-pre-wrap text-sm'>
                {waystarResponse.waystar_alerts}
              </div>
            </div>
          </div>
        )}

      <div>
        <label className='block text-sm font-medium mb-2'>
          Raw Waystar Response
        </label>
        <Textarea
          value={JSON.stringify(waystarResponse.waystar_response, null, 2)}
          readOnly
          className='font-mono text-xs h-32'
          placeholder='Raw Waystar response will appear here...'
        />
      </div>
    </>
  );
};

export default X12_271_Response_Display;
